import { DatePipe } from '@angular/common';
import {
  ApplicationRef,
  ComponentFactoryResolver,
  EmbeddedViewRef,
  Injectable,
  Injector,
  NgZone,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isEmpty, round } from 'lodash';
import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import {
  Content,
  DynamicContent,
  TableCell,
  TableLayout,
  TDocumentDefinitions,
} from 'pdfmake/interfaces';
import { concat, defer, EMPTY, Observable, Observer, of } from 'rxjs';
import { first, mergeMap, takeLast, tap } from 'rxjs/operators';
import { ProjectApiService } from 'src/app/core/services/project-api.service';
import { LoadFactory } from 'src/app/working-table/visualisation/loadFactory';
import { ProjectToSessionMapper } from 'src/app/working-table/working-table.component';
import { User } from '../objects/user';
import { MainView } from './../../working-table/visualisation/main-view';
import {
  CargoSpace,
  CargoSpaceSummary,
  Group,
  LoadingSpaceType,
  Session,
  SharedProject,
  WorkSpace,
} from './../types';
import { CargoSpacesSummaryService } from './cargo-spaces-summary.service';
import { ColorsService } from './colors.service';
import { LoaderService } from './loader.service';
import { ProjectService } from './project.service';
import { SharedProjectService } from './shared-project.service';
import { UserService } from './user.service';
import { WhiteLabelService } from './white-label.service';
import { CookieService } from 'ngx-cookie-service';
import { AxleLoadsComponent } from 'src/app/shared/components/axle-loads/axle-loads.component';
import domtoimage from 'dom-to-image';
import {
  convertGroupToCm,
  convertToNewWorkspaceCm,
} from '../../shared/helpers/unitRecalculationHelpers';
import { translateIndexedName } from '../../shared/helpers/indexed-name.helper';
import {
  getAllProjectCargoSpaces,
  getPdfCargoSpaceLoadGroups,
  getPdfLoadingStepGroups,
  isPalletContentsViewEnabled,
} from '../../shared/helpers/pallet-loading-space.helper';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';

(pdfMake as any).addVirtualFileSystem(pdfFonts);

const VIEW_WIDTH = 1500;
const VIEW_HEIGHT = 400;

@Injectable({
  providedIn: 'root',
})
export class PdfFromProjectService {
  private readonly MAIN_COLOR = '#174A71';
  private readonly MAIN_GRAY_COLOR = '#F8F8FA';
  private readonly GRAY_COLOR = '#b5bccb';
  private readonly WHITE_COLOR = '#ffffff';
  private user: User;
  private visualisation: MainView;
  private container: HTMLElement;
  private basicProjections: Projections;
  private data: SharedProject;
  private docDefinition: TDocumentDefinitions;
  private stops: WorkSpace[];

  constructor(
    private projectApiService: ProjectApiService,
    private sharedProjectService: SharedProjectService,
    private userService: UserService,
    private datePipe: DatePipe,
    private translateService: TranslateService,
    private colorsService: ColorsService,
    private cargoSpacesSummaryService: CargoSpacesSummaryService,
    private loaderService: LoaderService,
    private projectService: ProjectService,
    private zone: NgZone,
    private whiteLabelService: WhiteLabelService,
    private cookieService: CookieService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector,
  ) {
    this.userService.user.subscribe((user: User) => (this.user = user));
  }

  public generateSharePDF(
    data: SharedProject,
    workSpaceIndex?: number,
    imageData?: { imageSrc: string; currentWorkSpace: number },
    isBase64?: boolean,
    version?: number,
  ): Observable<any> {
    const isStops = data.project.workSpaces[0]?.stops?.length > 0;
    workSpaceIndex = isStops ? 0 : workSpaceIndex;
    this.visualisation = undefined;
    this.container = undefined;
    this.data = {
      ...data,
      project: { ...data.project },
    };
    this.stops = this.data.project.workSpaces[workSpaceIndex]?.stops;

    this.docDefinition = {
      defaultStyle: {
        color: this.MAIN_COLOR,
        fontSize: 8,
      },
      styles: {
        font10: {
          fontSize: 10,
        },
        bolded: {
          bold: true,
        },
      },
      content: [],
    };

    const observablesToCombine = [];
    if (!isNaN(workSpaceIndex) && !(this.stops?.length > 0)) {
      const workSpaceWiz = data.project.workSpaces[workSpaceIndex];
      const workSpace: WorkSpace = this.data.project.workSpaces[workSpaceIndex];
      observablesToCombine.push(defer(() => this.addHeader(workSpaceIndex, true)));
      observablesToCombine.push(
        defer(() => this.addLoadingSpaceContent(workSpace, workSpaceIndex)),
      );
      observablesToCombine.push(
        defer(() => this.addVisualisationScreen(workSpaceWiz, workSpaceIndex, imageData)),
      );
      observablesToCombine.push(defer(() => this.addAxleLoads(workSpace, true)));
      observablesToCombine.push(defer(() => this.addAxleLoads(workSpace, false)));
      observablesToCombine.push(defer(() => this.addLoadsGroups(workSpace, workSpaceIndex)));
      observablesToCombine.push(defer(() => this.addLoadsTable(workSpace, workSpaceIndex)));
      observablesToCombine.push(defer(() => this.addBigProjections()));
    } else if (!isNaN(workSpaceIndex) && this.stops?.length > 0) {
      this.stops.forEach((workSpace, index) => {
        observablesToCombine.push(defer(() => this.addHeader(index)));
        observablesToCombine.push(defer(() => this.addLoadingSpaceContent(workSpace, index, true)));
        observablesToCombine.push(
          defer(() => this.addVisualisationScreen(workSpace, index, imageData)),
        );
        observablesToCombine.push(defer(() => this.addAxleLoads(workSpace, true)));
        observablesToCombine.push(defer(() => this.addAxleLoads(workSpace, false)));
        observablesToCombine.push(defer(() => this.addPreviouslyLoadedLoads(index)));
        observablesToCombine.push(defer(() => this.addUnloadingLoadsTable(workSpace)));
        observablesToCombine.push(defer(() => this.addLoadsGroups(workSpace, index)));
        observablesToCombine.push(defer(() => this.addLoadsTable(workSpace, index)));
        observablesToCombine.push(defer(() => this.addBigProjections()));
      });
    } else {
      this.data.project.workSpaces.forEach((workSpace, index) => {
        observablesToCombine.push(defer(() => this.addHeader(index)));
        observablesToCombine.push(defer(() => this.addLoadingSpaceContent(workSpace, index, true)));
        observablesToCombine.push(
          defer(() => this.addVisualisationScreen(workSpace, index, imageData)),
        );
        observablesToCombine.push(defer(() => this.addAxleLoads(workSpace, true)));
        observablesToCombine.push(defer(() => this.addAxleLoads(workSpace, false)));
        observablesToCombine.push(defer(() => this.addLoadsGroups(workSpace, index)));
        observablesToCombine.push(defer(() => this.addLoadsTable(workSpace, index)));
        observablesToCombine.push(defer(() => this.addBigProjections()));
      });
    }
    return concat(
      defer(() => this.loaderService.open()),
      defer(() => this.createContainer()),
      defer(() => concat(...observablesToCombine)),
      defer(() => this.addFooter(isBase64, version)),
      defer(() => this.deleteVisualisation()),
      defer(() => this.loaderService.close()),
      defer(() => (!isBase64 ? this.downloadPdf() : this.downloadPdf(true))),
    ).pipe(takeLast(1));
  }

  private downloadPdf(isBase64 = false): Observable<any> {
    return new Observable((observer: Observer<any>): void => {
      if (isBase64) {
        pdfMake.createPdf(this.docDefinition).getBase64((base64String: string): void => {
          observer.next(base64String);
          observer.complete();
        });
      } else {
        pdfMake
          .createPdf(this.docDefinition)
          .download(`${this.data.name ? this.data.name : this.generateFileName()}.pdf`, () => {
            observer.next(null);
            observer.complete();
          });
      }
    });
  }

  private addHeader(workSpaceIndex: number, onlyOne?: boolean) {
    return this.getLogoDataUrl(this.data).pipe(
      first(),
      mergeMap((imageData: string) => {
        return this.sharedProjectService.isViewingShared.pipe(
          first(),
          tap((isViewingShared) => {
            let nameOrEmail: string;
            if (!isViewingShared) {
              if (this.user.firstName && this.user.lastName) {
                nameOrEmail = `${this.user.firstName} ${this.user.lastName}`;
              } else {
                nameOrEmail = this.user.email;
              }
            }

            const borderColor = [
              this.MAIN_COLOR,
              this.MAIN_COLOR,
              this.MAIN_COLOR,
              this.MAIN_COLOR,
            ];

            const contentObj: Content = {
              pageOrientation: 'portrait',
              columns: [
                {
                  layout: this.getPaddingLayout(),
                  table: {
                    widths: ['*'],
                    body: [
                      [
                        {
                          border: [false, false, false, true],
                          borderColor: borderColor,
                          layout: this.getPaddingLayout(),
                          table: {
                            widths: ['auto', '*'],
                            body: [
                              [
                                {
                                  border: [false, false, false, false],
                                  table: {
                                    widths: ['auto'],
                                    body: [
                                      [
                                        {
                                          border: [false, false, false, false],
                                          image: imageData,
                                          fit: [127, 30],
                                        },
                                      ],
                                    ],
                                  },
                                },
                                {
                                  margin: [0, 0, 0, 20],
                                  border: [false, false, false, false],
                                  table: {
                                    widths: ['*', 'auto', 'auto'],
                                    body: [
                                      [
                                        {
                                          border: [false, false, false, false],
                                          alignment: 'right',
                                          text: nameOrEmail,
                                          style: 'bolded',
                                        },
                                        {
                                          border: [false, false, false, false],
                                          alignment: 'right',
                                          text: this.user?.companyName
                                            ? '|     ' + this.user?.companyName
                                            : undefined,
                                        },
                                        {
                                          border: [false, false, false, false],
                                          alignment: 'right',
                                          text: '|     ' + this.data.name,
                                        },
                                      ],
                                    ],
                                  },
                                },
                              ],
                            ],
                          },
                        },
                      ],
                    ],
                  },
                },
              ],
            };
            if ((workSpaceIndex === 0 || onlyOne) && this.data.note) {
              const noteObj: Content = {
                margin: [0, 8, 0, 0],
                layout: this.getPaddingLayout(),
                table: {
                  headerRows: 1,
                  widths: ['*'],
                  body: [
                    [
                      {
                        border: [false, false, false, false],
                        text: this.data.note,
                        alignment: 'justify',
                      },
                    ],
                  ],
                },
              };

              (this.docDefinition.content as Content[]).push(contentObj);
              (this.docDefinition.content as Content[]).push(noteObj);
            } else if (workSpaceIndex !== 0 && !onlyOne) {
              contentObj['pageBreak'] = 'before';
              (this.docDefinition.content as Content[]).push(contentObj);
            } else {
              (this.docDefinition.content as Content[]).push(contentObj);
            }
          }),
        );
      }),
    );
  }

  private addLoadingSpaceContent(
    workSpace: WorkSpace,
    workSpaceIndex: number,
    isAllCargo?: boolean,
  ) {
    const table: Content = [];

    if (isAllCargo && workSpaceIndex === 0) {
      table.push(
        {
          margin: [0, 16, 0, 0],
          text: 'Summary:',
        },
        {
          style: 'font10',
          margin: [0, 6],
          text: this.generateLoadingSpacesDescription(),
          bold: false,
        },
      );
    }

    if (this.stops?.length > 0) {
      table.push({
        margin: [0, 16, 0, 0],
        text: 'Loading point:',
      });
      const stopNames = [];
      this.stops.forEach((stop, index) => {
        stopNames.push(
          {
            text: this.generateStopsDescription(stop, index),
            bold: workSpaceIndex === index ? true : false,
            decoration: workSpaceIndex === index ? 'underline' : undefined,
          },
          {
            text: '   ',
          },
        );
      });
      table.push({
        style: 'font10',
        margin: [0, 6],
        text: stopNames,
      });
    }

    if (this.stops?.length > 0) {
      const cargoSpacesSummary = this.cargoSpacesSummaryService.calculateCargoSpacesSummary(
        0,
        workSpaceIndex,
        this.data.project,
      );
      const stopSummary: CargoSpaceSummary = {
        freeLdm: 0,
        takenLdm: 0,
        loadedLoads: 0,
        notLoadedLoads: 0,
        occupiedVolume: 0,
        axis: [],
        freeVolume: 0,
        totalVolume: 0,
        totalWeight: 0,
        freeArea: 0,
      };
      cargoSpacesSummary.forEach((cargoSpace) => {
        stopSummary.freeLdm += cargoSpace.freeLdm;
        stopSummary.takenLdm += cargoSpace.takenLdm;
        stopSummary.loadedLoads += cargoSpace.loadedLoads;
        stopSummary.occupiedVolume += cargoSpace.occupiedVolume;
        stopSummary.totalWeight += cargoSpace.totalWeight;
        stopSummary.freeArea += cargoSpace.freeArea;
        stopSummary.freeVolume += cargoSpace.freeVolume;
        stopSummary.notLoadedLoads += cargoSpace.notLoadedLoads;
      });
      const unitWeight =
        this.data.project.options.unit === 'cm' || this.data.project.options.unit === 'mm'
          ? (this.translateService.instant('units_kg') as string)
          : (this.translateService.instant('units_lb') as string);
      const unitMeter =
        this.data.project.options.unit === 'cm' || this.data.project.options.unit === 'mm'
          ? 'm'
          : 'in';
      const borderColor = [this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR];
      table.push({
        margin: [0, 16, 0, 0],
        table: {
          widths: ['*', '*', '*'],
          headerRows: 1,
          body: [
            [
              {
                text: 'Summary',
                colSpan: 3,
                style: 'font10',
                bold: true,
                borderColor: borderColor,
                fillColor: this.MAIN_GRAY_COLOR,
                margin: 4,
              },
              {},
              {},
            ],
            [
              {
                text: [
                  { text: 'Space name: ' },
                  {
                    text: translateIndexedName(
                      workSpace.cargoSpaces[0].name,
                      (key) => this.translateService.instant(key) as string,
                    ),
                    bold: true,
                  },
                ],

                borderColor: borderColor,
                margin: [4, 2],
              },
              {
                text: [
                  { text: 'Dimensions: ' },
                  {
                    text: `${this.generateCargoSpaceDimensions(workSpace.cargoSpaces[0])} ${
                      this.data.project.options.unit
                    }`,
                    bold: true,
                  },
                ],
                borderColor: borderColor,
                margin: [4, 2],
              },
              {
                text: [
                  { text: 'Load capacity: ' },
                  { text: `${workSpace.cargoSpaces[0].limit} ${unitWeight}`, bold: true },
                ],
                borderColor: borderColor,
                margin: [4, 2],
              },
            ],
            [
              {
                text: [{ text: 'Loaded loads: ' }, { text: stopSummary.loadedLoads, bold: true }],
                borderColor: borderColor,
                margin: [4, 2],
              },

              {
                text: [
                  { text: 'Total weight of loads: ' },
                  { text: `${stopSummary.totalWeight} ${unitWeight}`, bold: true },
                ],
                borderColor: borderColor,
                margin: [4, 2],
              },

              {
                text: [
                  { text: 'Loading: ' },
                  { text: workSpace.cargoSpaces[0].trailerLoadingSide, bold: true },
                ],
                borderColor: borderColor,
                margin: [4, 2],
              },
            ],
            [
              {
                text: [
                  { text: 'Occupied LDM / Free LDM: ' },
                  { text: stopSummary.takenLdm, bold: true },
                  { text: ` / ${stopSummary.freeLdm}`, bold: true },
                ],
                borderColor: borderColor,
                margin: [4, 2],
              },
              {
                text: [
                  { text: 'Available space: ' },
                  {
                    text: [
                      {
                        text: `${stopSummary.freeArea} `,
                        bold: true,
                      },
                      { text: unitMeter, bold: true },
                      { text: '\u00B2', bold: true },
                    ],
                  },
                ],
                borderColor: borderColor,
                margin: [4, 2],
              },

              {
                text: [
                  { text: 'Used/remaining volume: ' },
                  {
                    text: [
                      {
                        text: `${stopSummary.occupiedVolume}/${stopSummary.freeVolume} `,
                        bold: true,
                      },
                      { text: unitMeter, bold: true },
                      { text: '\u00B3', bold: true },
                    ],
                  },
                ],
                borderColor: borderColor,
                margin: [4, 2],
              },
            ],
          ],
        },
      });
    } else {
      table.push(
        {
          margin: [0, 16, 0, 0],
          text: 'Loading Space:',
        },
        {
          style: 'font10',
          margin: [0, 6],
          text: [
            {
              text: translateIndexedName(
                workSpace.cargoSpaces[0].name,
                (key) => this.translateService.instant(key) as string,
              ),
              style: 'bolded',
            },
            {
              text: this.generateLoadingSpaceDescription(workSpace, workSpaceIndex),
              bold: false,
            },
          ],
        },
        {
          layout: this.getPaddingLayout(),
          table: {
            widths: ['auto', '*'],
            body: [
              [
                {
                  text: 'Loading:',
                  margin: [0, 0, 4, 16],
                  border: [false, false, false, true],
                  borderColor: this.MAIN_COLOR,
                },
                {
                  text: workSpace.cargoSpaces[0].trailerLoadingSide,
                  style: 'bolded',
                  margin: [0, 0, 0, 16],
                  border: [false, false, false, true],
                  borderColor: this.MAIN_COLOR,
                },
              ],
            ],
          },
        },
      );
    }

    return new Observable((observer: Observer<number>) => {
      observer.next((this.docDefinition.content as Content[]).push(...table));
      observer.complete();
    });
  }

  private addVisualisationScreen(
    workSpace: WorkSpace,
    workSpaceIndex: number,
    imageData: { imageSrc: string; currentWorkSpace: number },
  ) {
    const isUserImage =
      imageData && imageData.imageSrc && imageData.currentWorkSpace === workSpaceIndex;
    const showLoadsName = this.cookieService.get('loadName').includes('true');
    LoadFactory.clear();
    return this.getProjections(workSpace, undefined, showLoadsName).pipe(
      tap((projections: Projections) => {
        this.basicProjections = projections;
        if (isUserImage) {
          (this.docDefinition.content as Content[]).push({
            margin: [12, 12],
            alignment: 'center',
            image: imageData.imageSrc,
            fit: [300, 300],
          });
        } else {
          (this.docDefinition.content as Content[]).push({
            margin: [12, 12],
            alignment: 'center',
            stack: [
              {
                image: projections.default,
                width: 500,
                height: (VIEW_HEIGHT / VIEW_WIDTH) * 500,
                alignment: 'left',
              },
            ],
          });
        }
      }),
    );
  }

  private addAxleLoads(workSpace: WorkSpace, firstPart = true) {
    if (!workSpace.cargoSpaces[0]?.axis) {
      return EMPTY;
    }
    const isLoadingPart2 =
      (workSpace.axis ?? []).filter((axle) => axle.loadingSpacePart === 2).length > 0;
    if (!firstPart && !isLoadingPart2) {
      return EMPTY;
    }
    return this.getAxleLoad(workSpace, firstPart).pipe(
      tap((imageData: string) => {
        if (!imageData) {
          return;
        }
        const table: Content = {
          margin: [0, 0, 0, 20],
          table: {
            headerRows: 1,
            dontBreakRows: true,
            widths: ['*', 'auto'],
            body: [],
          },
        };

        const borderColor = [this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR];
        const axlesRows = this.generateAxleRows(workSpace, firstPart);
        const line: TableCell[] = [
          {
            border: [true, true, false, true],
            borderColor: borderColor,
            margin: [0, 4],
            table: {
              body: [...axlesRows],
            },
          },
          {
            border: [false, true, true, true],
            borderColor: borderColor,
            margin: [0, 4],
            image: imageData,
            fit: [130, 52],
          },
        ];

        table.table.body.push(line);

        (this.docDefinition.content as Content[]).push(table);
      }),
    );
  }

  private safeNumber(value: number | undefined | null): number {
    if (value === undefined || value === null) {
      return 0;
    }
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) || !isFinite(num) ? 0 : num;
  }

  private generateAxleRows(workSpace: WorkSpace, firstPart = true): TableCell[][] {
    const unit =
      this.data.project.options.unit === 'cm' || this.data.project.options.unit === 'mm'
        ? (this.translateService.instant('units_kg') as string)
        : (this.translateService.instant('units_lb') as string);

    const rows: TableCell[][] = [
      [
        {
          border: [false, false, false, false],
          text: 'Axle loads:',
        },
      ],
    ];

    if (workSpace.axis && workSpace.axis.length > 0) {
      const filteredAxles = workSpace.axis.filter((axle) => {
        return firstPart ? axle.loadingSpacePart === 1 : axle.loadingSpacePart === 2;
      });

      filteredAxles.forEach((axle, index) => {
        const addedLoadFinal = this.safeNumber(axle.addedLoad);
        const maxLoadFinal = this.safeNumber(axle.maxLoad);
        
        rows.push([
          {
            border: [false, false, false, false],
            text: `${index + 1}. ${addedLoadFinal} / ${maxLoadFinal} ${unit}`,
          },
        ]);
      });
    }

    return rows;
  }

  private getPreviouslyLoadedLoads(stops: WorkSpace[], index: number): Group[] {
    if (!stops || index === 0 || stops.length === 0) {
      return [];
    }

    const previouslyLoadedGroups: Group[] = [];
    for (let i = 0; i < index; i++) {
      stops[i].cargoSpaces?.forEach((cargoSpace: CargoSpace) => {
        getPdfCargoSpaceLoadGroups(cargoSpace).forEach((group: Group) => {
          if (!previouslyLoadedGroups.some((g) => g.id === group.id)) {
            previouslyLoadedGroups.push(group);
          }
        });
      });
    }
    const unloadedGroupsInPreviousStops: Group[][] = this.getPreviouslyUnloadedLoads(stops, index);
    let filteredPreviouslyLoadedGroups: Group[] = previouslyLoadedGroups.filter(
      (group) => !unloadedGroupsInPreviousStops[0].some((g) => g.id === group.id),
    );
    const splitGroups: Group[] = unloadedGroupsInPreviousStops[1];
    if (splitGroups.length > 0) {
      filteredPreviouslyLoadedGroups = this.changeQuantityOfPreviouslyLoadedGroups(
        filteredPreviouslyLoadedGroups,
        splitGroups,
      );
    }
    return filteredPreviouslyLoadedGroups;
  }

  private changeQuantityOfPreviouslyLoadedGroups(
    previouslyLoaded: Group[],
    splitGroups: Group[],
  ): Group[] {
    const updatedGroups: Group[] = JSON.parse(JSON.stringify(previouslyLoaded));
    updatedGroups.forEach((prevGroup: Group) => {
      const matchingSplitGroup = splitGroups.find((splitGroup) => splitGroup.id === prevGroup.id);
      if (matchingSplitGroup) {
        prevGroup.quantity -= matchingSplitGroup.quantity;
      }
    });
    return updatedGroups;
  }

  private getPreviouslyUnloadedLoads(stops: WorkSpace[], index: number): Group[][] {
    if (!stops || index === 0 || stops.length === 0) {
      return [];
    }

    let previouslyUnloadedGroups: Group[] = [];
    let previouslyUnloadedSplitGroups: Group[] = [];
    for (let i = 0; i < index; i++) {
      stops[i].unloadingSpace?.groups.forEach((group: Group) => {
        const isGroupSplit = stops[i].cargoSpaces.some((cargoSpace) =>
          cargoSpace.groups.some((g) => g.id === group.id),
        );
        if (!isGroupSplit) {
          previouslyUnloadedGroups = previouslyUnloadedGroups.concat(group);
        } else {
          previouslyUnloadedSplitGroups = previouslyUnloadedSplitGroups.concat(group);
        }
      });
    }
    return [previouslyUnloadedGroups, previouslyUnloadedSplitGroups];
  }

  private addPreviouslyLoadedLoads(index: number): Observable<any> {
    if (this.stops?.length > 0 && index === this.stops.length - 1) {
      return of();
    }
    const groups: Group[] = this.getPreviouslyLoadedLoads(this.stops, index);
    if (groups.length === 0) {
      return of();
    }
    const table: Content = {
      margin: [0, 0, 0, 20],
      table: {
        headerRows: 1,
        dontBreakRows: true,
        widths: ['*', 'auto', 'auto', 'auto'],
        body: [],
      },
    };
    table.table.body.push(this.generateLoadsGroupsHeaders('Previously loaded loads'));

    let quantity = 0;
    groups.forEach((group, index) => {
      quantity += group.quantity;
      table.table.body.push(this.generateLoadGroupRow(index, group));
    });

    const total: TableCell[] = [
      {
        fillColor: this.MAIN_COLOR,
        border: [false, false, false, false],
        text: 'Total',
        color: this.WHITE_COLOR,
        style: 'bolded',
        margin: [4, 2],
      },
      {
        fillColor: this.MAIN_COLOR,
        border: [false, false, false, false],
        text: '',
      },
      {
        fillColor: this.MAIN_COLOR,
        border: [false, false, false, false],
        text: '',
      },
      {
        fillColor: this.MAIN_COLOR,
        border: [false, false, false, false],
        text: quantity.toString(),
        alignment: 'center',
        color: this.WHITE_COLOR,
        style: 'bolded',
        margin: [0, 2],
      },
    ];
    table.table.body.push(total);

    const tablePusher = new Observable((observer: Observer<number>) => {
      observer.next((this.docDefinition.content as Content[]).push(table));
      observer.complete();
    });
    return defer(() => tablePusher);
  }

  private addLoadsGroups(workSpace: WorkSpace, index: number): Observable<any> {
    if (this.stops?.length > 0 && index === this.stops.length - 1) {
      return;
    }

    let groups: Group[] = [];
    workSpace.cargoSpaces.forEach((cargoSpace: CargoSpace): void => {
      groups = groups.concat(getPdfCargoSpaceLoadGroups(cargoSpace));
    });
    const previouslyLoadedLoads: Group[] = this.getPreviouslyLoadedLoads(this.stops, index);
    const groupsWithoutPreviouslyLoaded: Group[] = groups.filter(
      (group) => !previouslyLoadedLoads.some((g: Group): boolean => g.id === group.id),
    );
    if (groupsWithoutPreviouslyLoaded.length === 0) {
      return of();
    }
    groups = groupsWithoutPreviouslyLoaded;

    const table: Content = {
      margin: [0, 0, 0, 20],
      table: {
        headerRows: 1,
        dontBreakRows: true,
        widths: ['*', 'auto', 'auto', 'auto'],
        body: [],
      },
    };

    table.table.body.push(this.generateLoadsGroupsHeaders());

    let quantity = 0;
    groups.forEach((group, index) => {
      quantity += group.quantity;
      table.table.body.push(this.generateLoadGroupRow(index, group));
    });

    const total: TableCell[] = [
      {
        fillColor: this.MAIN_COLOR,
        border: [false, false, false, false],
        text: 'Total',
        color: this.WHITE_COLOR,
        style: 'bolded',
        margin: [4, 2],
      },
      {
        fillColor: this.MAIN_COLOR,
        border: [false, false, false, false],
        text: '',
      },
      {
        fillColor: this.MAIN_COLOR,
        border: [false, false, false, false],
        text: '',
      },
      {
        fillColor: this.MAIN_COLOR,
        border: [false, false, false, false],
        text: quantity.toString(),
        alignment: 'center',
        color: this.WHITE_COLOR,
        style: 'bolded',
        margin: [0, 2],
      },
    ];
    table.table.body.push(total);

    const tablePusher = new Observable((observer: Observer<number>) => {
      observer.next((this.docDefinition.content as Content[]).push(table));
      observer.complete();
    });
    return defer(() => tablePusher);
  }

  private generateLoadsGroupsHeaders(headerTittle = 'New loaded loads'): TableCell[] {
    return [
      {
        text: headerTittle,
        borderColor: [this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR],
        fillColor: this.MAIN_GRAY_COLOR,
        margin: [4, 2],
      },
      {
        text: `Load dimensions  [${
          this.data.project.options.unit === 'cm' || this.data.project.options.unit === 'mm'
            ? this.data.project.options.unit === 'mm'
              ? (this.translateService.instant('units_mm') as string)
              : (this.translateService.instant('units_cm') as string)
            : (this.translateService.instant('units_in') as string)
        }]`,
        borderColor: [this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR],
        fillColor: this.MAIN_GRAY_COLOR,
        alignment: 'center',
        margin: [0, 2],
      },
      {
        text: `Weight [${
          this.data.project.options.unit === 'cm' || this.data.project.options.unit === 'mm'
            ? (this.translateService.instant('units_kg') as string)
            : (this.translateService.instant('units_lb') as string)
        }]`,
        borderColor: [this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR],
        fillColor: this.MAIN_GRAY_COLOR,
        alignment: 'center',
        margin: [0, 2],
      },
      {
        text: 'Quantity',
        borderColor: [this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR],
        fillColor: this.MAIN_GRAY_COLOR,
        alignment: 'center',
        margin: [0, 2],
      },
    ];
  }

  private generateLoadGroupRow(index: number, group: Group): TableCell[] {
    const borderColor = [this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR];
    return [
      {
        color: this.MAIN_COLOR,
        borderColor: borderColor,
        width: '*',
        margin: [0, 2],
        columns: [
          { text: `${index + 1}.`, width: 'auto', margin: [4, 2] },
          {
            width: 'auto',
            margin: [4, 2],
            stack: [
              {
                image:
                  this.stops?.length > 0
                    ? this.colorsService.getMultistopImageWithDestination(
                        this.getStopLetterFromIndex(group.destination.id - 1),
                        group.color,
                      )
                    : this.colorsService.getDrawedCircle(group.color),
                width: 10,
                height: 10,
              },
            ],
          },
          {
            text: this.translateService.instant(group.name) as string,
            margin: [4, 2],
          },
        ],
      },
      {
        color: this.MAIN_COLOR,
        borderColor: borderColor,
        text: this.generateGroupDimensions(group),
        alignment: 'center',
        margin: [4, 4],
      },
      {
        color: this.MAIN_COLOR,
        borderColor: borderColor,
        text: group.weight.toString(),
        alignment: 'center',
        margin: [4, 4],
      },
      {
        color: this.MAIN_COLOR,
        borderColor: borderColor,
        text: group.quantity.toString(),
        alignment: 'center',
        margin: [4, 4],
      },
    ];
  }

  private addLoadsTable(workSpace: WorkSpace, index: number) {
    if (this.stops?.length > 0 && index === this.stops.length - 1) {
      return;
    }
    let groups: Group[] = [];
    workSpace.cargoSpaces.forEach((cargoSpace: CargoSpace) => {
      groups = groups.concat(getPdfLoadingStepGroups(cargoSpace));
    });
    if (groups.length === 0) {
      return of();
    }
    groups = this.projectService.getProjectGroupOrder(groups);

    const table: Content = {
      margin: [0, 0, 0, 20],
      table: {
        headerRows: 1,
        dontBreakRows: true,
        widths: ['*', 'auto'],
        body: [],
      },
    };
    table.table.body.push(this.generateLoadsTableHeaders());

    LoadFactory.clear();
    const newGroup: Group[][] = [];
    const observers = [];
    groups.forEach((group, index) => {
      if (isEmpty(newGroup)) {
        newGroup.push([group]);
      } else {
        newGroup.push([...newGroup[index - 1], group]);
      }

      observers.push(
        defer(() =>
          this.getProjections(workSpace, newGroup[index], false).pipe(
            tap((projections: Projections) => {
              return table.table.body.push(
                this.generateGroupRow(index, group, projections.default) as Content[] & TableCell[],
              );
            }),
          ),
        ),
      );
    });

    const tablePusher = new Observable((observer: Observer<number>) => {
      observer.next((this.docDefinition.content as Content[]).push(table)), observer.complete();
    });
    return concat(
      defer(() => concat(...observers)),
      defer(() => tablePusher),
    );
  }

  private generateLoadsTableHeaders(): TableCell[] {
    const borderColor = [this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR];
    return [
      {
        text: 'Order of arranging cargo:',
        border: [true, true, false, true],
        borderColor: borderColor,
        fillColor: this.MAIN_GRAY_COLOR,
        margin: [4, 2],
      },
      {
        text: 'Quantity',
        border: [false, true, true, true],
        borderColor: borderColor,
        fillColor: this.MAIN_GRAY_COLOR,
        margin: [0, 2],
      },
    ];
  }

  private generateGroupRow(index: number, group: Group, screenshot?: string): TableCell[] {
    const borderColor = [this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR, this.GRAY_COLOR];
    const totalQuantity = group.quantity;
    return [
      {
        border: [true, true, false, true],
        borderColor: borderColor,
        columns: [
          { text: `${index + 1}.`, width: 'auto', margin: [4, 10] },
          {
            width: 'auto',
            margin: [4, 10],
            stack: [
              {
                image:
                  this.stops?.length > 0
                    ? this.colorsService.getMultistopImageWithDestination(
                        this.getStopLetterFromIndex(group.destination.id - 1),
                        group.color,
                      )
                    : this.colorsService.getDrawedCircle(group.color),
                width: 10,
                height: 10,
              },
            ],
          },
          {
            width: '*',
            text: this.translateService.instant(group.name) as string,
            margin: [4, 10],
          },
          screenshot
            ? { image: screenshot, width: 125, height: 25, alignment: 'center', margin: [0, 2] }
            : { text: '', width: 125, height: 25, alignment: 'center' },
        ],
      },
      {
        border: [false, true, true, true],
        borderColor: borderColor,
        text: totalQuantity.toString(),
        alignment: 'center',
        margin: [0, 10],
      },
    ];
  }

  private addUnloadingLoadsTable(workSpace: WorkSpace) {
    const groups = workSpace.unloadingSpace.groups;

    if (!(groups?.length > 0)) {
      return;
    }
    const table: Content = {
      margin: [0, 0, 0, 20],
      table: {
        headerRows: 1,
        dontBreakRows: true,
        widths: ['*', 'auto', 'auto', 'auto'],
        body: [],
      },
    };
    table.table.body.push(this.generateLoadsGroupsHeaders('Loads to be unloaded'));
    let quantity = 0;
    groups.forEach((group, index) => {
      quantity += group.quantity;
      table.table.body.push(this.generateLoadGroupRow(index, group));
    });

    const total: TableCell[] = [
      {
        fillColor: this.MAIN_COLOR,
        border: [false, false, false, false],
        text: 'Total',
        color: this.WHITE_COLOR,
        style: 'bolded',
        margin: [4, 2],
      },
      {
        fillColor: this.MAIN_COLOR,
        border: [false, false, false, false],
        text: '',
      },
      {
        fillColor: this.MAIN_COLOR,
        border: [false, false, false, false],
        text: '',
      },
      {
        fillColor: this.MAIN_COLOR,
        border: [false, false, false, false],
        text: quantity.toString(),
        alignment: 'center',
        color: this.WHITE_COLOR,
        style: 'bolded',
        margin: [0, 2],
      },
    ];
    table.table.body.push(total);
    const tablePusher = new Observable((observer: Observer<number>) => {
      observer.next((this.docDefinition.content as Content[]).push(table)), observer.complete();
    });
    return defer(() => tablePusher);
  }

  private addBigProjections() {
    return this.getLogoDataUrl(this.data).pipe(
      first(),
      tap((logoUrl) => {
        const table: Content = {
          pageBreak: 'before',
          layout: 'noBorders',
          table: {
            widths: ['100%'],
            body: [
              [
                {
                  border: [false, false, false, false],
                  image: logoUrl,
                  fit: [127, 30],
                },
              ],
              [this.generateBigProjection('Default', this.basicProjections.default)],
              [this.generateBigProjection('Top', this.basicProjections.top)],
              [this.generateBigProjection('Left', this.basicProjections.left)],
              [this.generateBigProjection('Right', this.basicProjections.right)],
            ],
          },
        };
        (this.docDefinition.content as Content[]).push(table);
      }),
    );
  }

  private generateBigProjection(headerText: string, screenshot: string): TableCell {
    return {
      layout: 'noBorders',
      table: {
        body: [
          [
            {
              table: {
                widths: ['auto', '*'],
                body: [
                  [
                    {
                      border: [false, true, false, false],
                      borderColor: [
                        this.MAIN_COLOR,
                        this.MAIN_COLOR,
                        this.MAIN_COLOR,
                        this.MAIN_COLOR,
                      ],
                      text: headerText,
                      fillColor: this.MAIN_COLOR,
                      color: this.WHITE_COLOR,
                      margin: [8, 2],
                      style: 'bolded',
                    },
                    {
                      text: '',
                      border: [false, true, false, false],
                      borderColor: [
                        this.MAIN_COLOR,
                        this.MAIN_COLOR,
                        this.MAIN_COLOR,
                        this.MAIN_COLOR,
                      ],
                    },
                  ],
                ],
              },
            },
          ],
          [
            {
              border: [false, false, false, false],
              image: screenshot,
              width: 500,
              height: (VIEW_HEIGHT / VIEW_WIDTH) * 500,
              alignment: 'center',
              margin: [0, 8],
            },
          ],
        ],
      },
    };
  }

  private addFooter(isBase64 = false, version: number) {
    return this.sharedProjectService.isViewingShared.pipe(
      first(),
      mergeMap((isViewingShared) => {
        if (isBase64 || isViewingShared) {
          return this.sharedProjectService.sharedProjectId.pipe(
            first(),
            tap((id) => (this.docDefinition.footer = this.generateFooterFromId(id, version))),
          );
        } else {
          return this.projectApiService.share().pipe(
            first(),
            tap(
              (id: string) => (this.docDefinition.footer = this.generateFooterFromId(id, version)),
            ),
          );
        }
      }),
    );
  }

  private generateFooterFromId(sessionId: string, version: number): DynamicContent {
    return () => ({
      columns: [
        { width: '*', text: '' },
        {
          width: 'auto',
          table: {
            body: [
              [
                {
                  border: [true, true, true, true],
                  borderColor: [this.MAIN_COLOR, this.MAIN_COLOR, this.MAIN_COLOR, this.MAIN_COLOR],
                  margin: [16, 8],
                  text: this.generateLink(sessionId, version),
                  link: this.generateLink(sessionId, version),
                },
              ],
            ],
          },
        },
        { width: '*', text: '' },
      ],
    });
  }

  private generateLink(sessionId: string, version: number): string {
    const isVersion: boolean = version !== null && version !== undefined;
    if (isVersion) {
      return `${window.location.origin}/session/?id=${sessionId}&version=${version}`;
    } else {
      return `${window.location.origin}/session/?id=${sessionId}`;
    }
  }

  private generateGroupDimensions(group: Group): string {
    if (group.loadType === 0) {
      return `${group.dimensions.length}x${group.dimensions.width}x${group.dimensions.height}`;
    } else if (group.loadType === 1) {
      return `${group.dimensions.height}x${group.dimensions.diameter}`;
    } else if (group.loadType === 2) {
      return `${group.dimensions.length}x${group.dimensions.diameter}`;
    }
  }

  private generateCargoSpaceDimensions(cargoSpace: CargoSpace): string {
    return `${cargoSpace.dimensions.length}x${cargoSpace.dimensions.width}x${cargoSpace.dimensions.height}`;
  }

  private generateLoadingSpaceDescription(workSpace: WorkSpace, workSpaceIndex: number): string {
    const summary = this.cargoSpacesSummaryService.calculateCargoSpacesSummary(workSpaceIndex);
    const weight = summary.reduce((a: number, b: CargoSpaceSummary) => a + b.totalWeight, 0);
    const freeLdm = summary.reduce((a: number, b: CargoSpaceSummary) => a + b.freeLdm, 0);
    const loadingSpace = workSpace.cargoSpaces[0];
    const unitWeight =
      this.data.project.options.unit === 'cm' || this.data.project.options.unit === 'mm'
        ? (this.translateService.instant('units_kg') as string)
        : (this.translateService.instant('units_lb') as string);

    let text =
      ` ${this.generateCargoSpaceDimensions(loadingSpace)} ${this.data.project.options.unit} |  ` +
      `Weight: ${weight} ${unitWeight}  |  ` +
      `Free LDM: ${round(freeLdm, 2)}`;

    if (loadingSpace.limit) {
      text += ` | Load capacity: ${loadingSpace.limit} ${unitWeight}`;
    }
    return text;
  }

  private generateLoadingSpacesDescription(): string {
    const summary = this.cargoSpacesSummaryService.calculateWorkSpacesSummary(this.data.project);
    const unitWeight =
      this.data.project.options.unit === 'cm' || this.data.project.options.unit === 'mm'
        ? (this.translateService.instant('units_kg') as string)
        : (this.translateService.instant('units_lb') as string);

    const text =
      `Number of loads: ${summary.loadedLoads}  |  ` +
      `Weight of loads: ${summary.totalWeight} ${unitWeight}  |  ` +
      `Cargo spaces: ${this.data.project.workSpaces.length}`;
    return text;
  }

  private generateStopsDescription(stop: WorkSpace, stopIndex: number): string {
    return `${this.getStopLetterFromIndex(stopIndex)}  |  ${stop.name}`;
  }

  private getStopLetterFromIndex(stopIndex: number) {
    return (stopIndex + 10).toString(36).toLocaleUpperCase();
  }

  private getPaddingLayout(): TableLayout {
    return {
      paddingLeft: () => {
        return 0;
      },
      paddingRight: () => {
        return 0;
      },
      paddingTop: () => {
        return 0;
      },
      paddingBottom: () => {
        return 0;
      },
    };
  }

  private generateFileName(): string {
    return `GL${this.datePipe.transform(Date.now(), 'yyMMddHHmm')}`;
  }

  private getLogoDataUrl(data: SharedProject) {
    const image = new Image();
    image.crossOrigin = 'Anonymous';

    const logo = new Observable((observer: Observer<string>) => {
      image.onload = function () {
        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;

        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0);

        observer.next(canvas.toDataURL('image/png'));
        observer.complete();
      };
    });
    const whiteLabel = this.whiteLabelService.getState();
    if (data && this.data.project.companyBranding) {
      image.src = this.data.project.companyBranding.logoUrl;
    } else if (whiteLabel && whiteLabel.logoUrl) {
      image.src = whiteLabel.logoUrl;
    } else {
      image.src = '/assets/svg/logo-dark-color-with-text.svg';
    }
    return logo;
  }

  private createContainer() {
    this.container = document.createElement('div');
    this.container.style.position = 'fixed';
    this.container.style.top = `-${VIEW_HEIGHT * 2}px`;
    this.container.style.left = `-${VIEW_WIDTH * 2}px`;
    this.container.style.width = `${VIEW_WIDTH}px`;
    this.container.style.minHeight = `${VIEW_HEIGHT}px`;
    document.body.appendChild(this.container);
  }

  private getProjections(workSpace: WorkSpace, group?: Group[], showLoadsName?: boolean) {
    workSpace.cargoSpaces[0].axis = workSpace.axis;
    const isMm = this.data.project.options.unit === 'mm';
    const allCargoSpaces = getAllProjectCargoSpaces(this.data.project);
    const showPalletContents = isPalletContentsViewEnabled((name) => this.cookieService.get(name));
    const session = new ProjectToSessionMapper(
      workSpace.cargoSpaces,
      workSpace.outsideSpace,
      this.data.project.options.unit,
      undefined,
      allCargoSpaces,
      showPalletContents,
    );
    const workSpaceConverted: WorkSpace = convertToNewWorkspaceCm(workSpace);
    const session2 = new ProjectToSessionMapper(
      workSpaceConverted.cargoSpaces,
      workSpaceConverted.outsideSpace,
      this.data.project.options.unit,
      undefined,
      allCargoSpaces,
      showPalletContents,
    );
    if (group) {
      session.groups = isMm ? convertGroupToCm(group) : group;
      session2.groups = isMm ? convertGroupToCm(group) : group;
    }
    if (this.visualisation) {
      const sessionData: Session = {
        ...session,
        groups: session.groups.map((group: Group) => {
          return {
            ...group,
            showLoadName: showLoadsName,
          } as Group;
        }),
      };
      const sessionData2: Session = {
        ...session2,
        groups: session2.groups.map((group: Group) => {
          return {
            ...group,
            showLoadName: showLoadsName,
          } as Group;
        }),
      };
      const sessionConverted = isMm ? sessionData2 : sessionData;
      this.visualisation.setSession(sessionConverted, true);
    } else {
      LoadFactory.clear();
      const projectOptions = this.data.project.options;
      projectOptions.backlightOn = false;
      projectOptions.measure = false;
      this.visualisation = new MainView(
        this.container,
        isMm ? session2 : session,
        this.projectService,
        true,
        projectOptions,
        this.zone,
        this.cookieService,
        this.sharedProjectService,
      );
    }
    const projections = new Projections();
    this.visualisation.setPrintPerspectiveCamera(showLoadsName);
    projections.default = this.visualisation.getImageData();
    this.visualisation.setTopViewIn2D();
    projections.top = this.visualisation.getImageData();
    this.visualisation.setLeftViewIn2D();
    projections.left = this.visualisation.getImageData();
    this.visualisation.setRightViewIn2D();
    projections.right = this.visualisation.getImageData();
    return new Observable((observer) => {
      observer.next(projections);
      observer.complete();
    });
  }

  private deleteVisualisation() {
    LoadFactory.clear();
    this.visualisation.loseContext();
    document.body.removeChild(this.container);
  }

  private createLoadingSpace(workSpace: WorkSpace): LoadingSpaceType {
    const cargoSpace = workSpace.cargoSpaces[0];
    const cargoSpace2 = workSpace.cargoSpaces[1];
    return {
      length: cargoSpace.dimensions.length,
      width: cargoSpace.dimensions.width,
      height: cargoSpace.dimensions.height,
      length1: cargoSpace2?.dimensions?.length,
      width1: cargoSpace2?.dimensions?.width,
      height1: cargoSpace2?.dimensions?.height,

      name: cargoSpace.name,
      limit: cargoSpace.limit,
      axis: workSpace.axis || [],
      set: cargoSpace2 !== undefined,
      type: cargoSpace.type,
    };
  }

  private getAxleLoad(workSpace: WorkSpace, firstPart = true) {
    // Ensure projectService has the project set so component's subscription works
    this.projectService.setState(this.data.project);
    
    const componentRef = this.componentFactoryResolver
      .resolveComponentFactory(AxleLoadsComponent)
      .create(this.injector);
    const axles = workSpace.axis ?? [];
    const partAxis1 = axles.filter((axis) => axis.loadingSpacePart === 1);
    const partAxis2 = axles.filter((axis) => axis.loadingSpacePart === 2);
    const axis = firstPart ? partAxis1 : partAxis2;
    const loadingSpace = this.createLoadingSpace(workSpace);

    if (firstPart) {
      componentRef.instance.numberOfAxles = [1, 2];
      componentRef.instance.axles = axis;
      componentRef.instance.loadingSpace = loadingSpace;
      componentRef.instance.isPrint = true;
      componentRef.instance.isSwitchSet = !firstPart;

      componentRef.instance.form = new UntypedFormGroup({
        '1': new UntypedFormControl(0),
        '2': new UntypedFormControl(0),
      });
    } else {
      componentRef.instance.axles = axis;
      componentRef.instance.loadingSpace = loadingSpace;
      componentRef.instance.isPrint = true;
      componentRef.instance.isSwitchSet = !firstPart;
      componentRef.instance.selectedAxle = axis.length > 0 ? 1 : 0;
    }

    this.appRef.attachView(componentRef.hostView);

    const domElem = (componentRef.hostView as EmbeddedViewRef<HTMLElement>)
      .rootNodes[0] as HTMLElement;

    domElem.style.position = 'fixed';
    domElem.style.top = `-700px`;
    domElem.style.left = `-700px`;
    domElem.style.width = `350px`;
    domElem.style.minHeight = `140px`;
    document.body.appendChild(domElem);

    // Trigger change detection to run lifecycle hooks
    this.appRef.tick();

    setTimeout(() => {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
    }, 3000);

    return new Observable((observer: Observer<string>) => {
      const timeout = firstPart ? 0 : 1200;
      
      setTimeout(() => {
        // Ensure all change detection is complete
        this.appRef.tick();
        
        if (!firstPart) {
          setTimeout(() => {
            this.appRef.tick();
            this.captureImage(domElem, observer);
          }, 300);
        } else {
          this.captureImage(domElem, observer);
        }
      }, timeout);
    });
  }

  private captureImage(domElem: HTMLElement, observer: Observer<string>): void {
    domtoimage
      .toPng(domElem, {
        style: {
          left: '0',
          right: '0',
          bottom: '0',
          top: '0',
        },
      })
      .then((imageUrl) => {
        if (imageUrl) {
          observer.next(imageUrl);
        }
        observer.complete();
      })
      .catch((error) => {
        console.error('Error generating axle load image:', error);
        observer.complete();
      });
  }
}

interface Projections {
  default: string;
  top: string;
  left: string;
  right: string;
}

class Projections implements Projections {}
